/**
 * DOCX Export Utility
 * 
 * Converts AeroDocument JSON to Microsoft Word format using docx library.
 * Applies FAA/EASA document formatting standards.
 */

import { 
  Document, 
  Packer, 
  Paragraph, 
  TextRun,
  ImageRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
  UnderlineType,
  Header,
  Footer,
  convertInchesToTwip,
  ShadingType
} from 'docx';
import type { 
  AeroDocument, 
  DocumentSection, 
  ContentBlock,
  ParagraphContent,
  ListContent,
  TableContent,
  TableColumn,
  HeadingContent,
  ImageContent,
  CodeBlockContent,
  VectorGraphicContent
} from '../types/document-schema';
import { buildTocEntries } from './docx-export-helpers';
import { logger } from './logger';

/**
 * Generate timestamp string for filenames (matches PDF generator format)
 */
function generateTimestamp(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `${year}${month}${day}-${hours}${minutes}${seconds}`;
}

/**
 * Export AeroDocument to DOCX format
 */
export async function exportToDocx(aeroDocument: AeroDocument, filename?: string): Promise<void> {
  try {
    const doc = createDocxDocument(aeroDocument);
    const blob = await Packer.toBlob(doc);
    
    // Ensure the blob has the correct MIME type for Word documents
    const docxBlob = new Blob([blob], { 
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
    });
    
    // Generate filename with timestamp (matches PDF naming pattern)
    const timestamp = generateTimestamp();
    const defaultFilename = `${aeroDocument.metadata.documentNumber}-${aeroDocument.metadata.currentRevision}-${timestamp}.docx`;
    
    // Trigger download using window.document to avoid shadowing
    const url = URL.createObjectURL(docxBlob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = filename || defaultFilename;
    link.style.display = 'none';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    
    // Delay URL revocation to ensure browser finishes reading the blob
    // This prevents race condition that can cause corrupted downloads
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    
    logger.debug('[DOCX Export] Document exported successfully');
  } catch (error) {
    logger.error('[DOCX Export] Export failed:', error);
    throw error;
  }
}

/**
 * Create DOCX document from AeroDocument with FAA formatting
 */
function createDocxDocument(aeroDoc: AeroDocument): Document {
  // Section children can be Paragraph or Table (FileChild type in docx library)
  const sections: (Paragraph | Table)[] = [];
  
  // FAA-compliant cover page
  sections.push(
    new Paragraph({ text: '', spacing: { after: 800 } }), // Top spacing
    new Paragraph({
      text: aeroDoc.metadata.documentNumber,
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),
    new Paragraph({
      text: aeroDoc.metadata.projectSpecificTitle || aeroDoc.metadata.documentTitle,
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      spacing: { after: 600 }
    }),
    new Paragraph({ text: '', spacing: { after: 400 } }), // Spacing
    new Paragraph({
      children: [
        new TextRun({ text: 'Revision: ', bold: true }),
        new TextRun({ text: aeroDoc.metadata.currentRevision })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: 'Status: ', bold: true }),
        new TextRun({ text: aeroDoc.metadata.status })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: 'Date: ', bold: true }),
        new TextRun({ text: new Date(aeroDoc.metadata.revisionDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({ text: 'Project: ', bold: true }),
        new TextRun({ text: aeroDoc.metadata.projectCode || 'N/A' })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    })
  );
  
  // Add revision history table if available (optional)
  // Access as 'any' since revisionHistory may exist at runtime but isn't in the base type
  const metadata = aeroDoc.metadata as { revisionHistory?: Array<{ revision?: string; date?: string; description?: string; author?: string }> };
  if (metadata.revisionHistory && Array.isArray(metadata.revisionHistory) && metadata.revisionHistory.length > 0) {
    sections.push(
      new Paragraph({ text: '', spacing: { after: 600 } }),
      new Paragraph({
        children: [
          new TextRun({ text: 'Document Revision History', bold: true, underline: { type: UnderlineType.SINGLE } })
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 300 }
      })
    );
    
    // Create revision history table
    const revisionTableRows: TableRow[] = [
      // Header row
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: 'Rev', bold: true, color: 'FFFFFF' })] })],
            shading: { type: ShadingType.SOLID, fill: '4472C4', color: '4472C4' },
            width: { size: 10, type: WidthType.PERCENTAGE }
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: 'Date', bold: true, color: 'FFFFFF' })] })],
            shading: { type: ShadingType.SOLID, fill: '4472C4', color: '4472C4' },
            width: { size: 20, type: WidthType.PERCENTAGE }
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: 'Description', bold: true, color: 'FFFFFF' })] })],
            shading: { type: ShadingType.SOLID, fill: '4472C4', color: '4472C4' },
            width: { size: 50, type: WidthType.PERCENTAGE }
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: 'Author', bold: true, color: 'FFFFFF' })] })],
            shading: { type: ShadingType.SOLID, fill: '4472C4', color: '4472C4' },
            width: { size: 20, type: WidthType.PERCENTAGE }
          })
        ]
      })
    ];
    
    // Data rows
    for (const rev of metadata.revisionHistory) {
      revisionTableRows.push(
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: rev.revision || '' })] }),
            new TableCell({ children: [new Paragraph({ text: rev.date || '' })] }),
            new TableCell({ children: [new Paragraph({ text: rev.description || '' })] }),
            new TableCell({ children: [new Paragraph({ text: rev.author || '' })] })
          ]
        })
      );
    }
    
    sections.push(
      new Table({
        rows: revisionTableRows,
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 2 },
          bottom: { style: BorderStyle.SINGLE, size: 2 },
          left: { style: BorderStyle.SINGLE, size: 2 },
          right: { style: BorderStyle.SINGLE, size: 2 },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
          insideVertical: { style: BorderStyle.SINGLE, size: 1 }
        }
      })
    );
  }
  
  sections.push(
    new Paragraph({ text: '', pageBreakBefore: true }) // Page break after cover page
  );
  
  // Table of Contents
  sections.push(
    new Paragraph({
      text: 'Table of Contents',
      heading: HeadingLevel.HEADING_1,
      spacing: { after: 400 }
    })
  );
  
  // Build TOC entries from document sections (if body exists)
  const topLevelSections = aeroDoc.body ? aeroDoc.body.filter(s => !s.parent) : [];
  if (topLevelSections.length > 0) {
    for (const section of topLevelSections) {
      buildTocEntries(section, aeroDoc.body, sections, 0);
    }
  } else {
    sections.push(
      new Paragraph({
        children: [
          new TextRun({ text: '(No sections available)', italics: true, color: '999999' })
        ],
        spacing: { after: 200 }
      })
    );
  }
  
  sections.push(
    new Paragraph({ text: '', spacing: { after: 400 } }),
    new Paragraph({
      children: [
        new TextRun({ 
          text: 'Note: This document was generated from the Aerotage Avionics Engineering System.',
          size: 18,
          italics: true,
          color: '666666'
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({ text: '', pageBreakBefore: true })
  );
  
  // Document body sections (reuse topLevelSections from TOC)
  if (topLevelSections.length > 0) {
    for (const section of topLevelSections) {
      renderSectionToDocx(section, aeroDoc.body, sections);
    }
  } else {
    // Fallback if no sections
    sections.push(
      new Paragraph({
        text: 'Document Content',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 400 }
      }),
      new Paragraph({
        children: [
          new TextRun({ 
            text: 'This document does not have structured content yet.',
            italics: true,
            color: '999999'
          })
        ]
      })
    );
  }
  
  // Create document with numbering definitions for lists
  // NOTE: Complete numbering definition required to prevent Word memory issues
  return new Document({
    numbering: {
      config: [
        {
          reference: 'default-numbering',
          levels: [
            {
              level: 0,
              format: 'decimal',
              text: '%1.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) }
                }
              }
            },
            {
              level: 1,
              format: 'lowerLetter',
              text: '%2.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: { left: convertInchesToTwip(1), hanging: convertInchesToTwip(0.25) }
                }
              }
            },
            {
              level: 2,
              format: 'lowerRoman',
              text: '%3.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: { left: convertInchesToTwip(1.5), hanging: convertInchesToTwip(0.25) }
                }
              }
            }
          ]
        }
      ]
    },
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertInchesToTwip(1),
            bottom: convertInchesToTwip(1),
            left: convertInchesToTwip(1),
            right: convertInchesToTwip(1)
          }
        }
      },
      headers: {
        default: new Header({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: `${aeroDoc.metadata.documentNumber || ''} Rev ${aeroDoc.metadata.currentRevision || 'Draft'}`,
                  size: 20,
                  color: '666666'
                })
              ],
              alignment: AlignmentType.RIGHT
            })
          ]
        })
      },
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: `${aeroDoc.metadata.projectCode || 'Aerotage'}`,
                  size: 20,
                  color: '666666'
                })
              ],
              alignment: AlignmentType.CENTER
            })
          ]
        })
      },
      children: sections
    }]
  });
}

/**
 * Recursively render section and children to DOCX
 * @param visited - Set of already visited section IDs to prevent infinite loops
 */
function renderSectionToDocx(
  section: DocumentSection,
  allSections: DocumentSection[],
  elements: (Paragraph | Table)[],
  visited: Set<string> = new Set()
): void {
  // Safety check
  if (!section) return;
  
  // Prevent circular references - if already visited, skip
  if (visited.has(section.id)) {
    logger.warn(`[DOCX Export] Circular reference detected, skipping section: ${section.id}`);
    return;
  }
  visited.add(section.id);
  
  // Limit recursion depth to prevent runaway memory
  if (visited.size > 1000) {
    logger.error('[DOCX Export] Maximum section depth exceeded (1000), stopping recursion');
    return;
  }
  
  // Section heading - derive level from section number for correct hierarchy depth
  const headingLevel = getHeadingLevelFromSectionNumber(section.number);
  const sectionNumber = section.number || '';
  const sectionTitle = section.title || 'Untitled Section';
  
  elements.push(
    new Paragraph({
      text: `${sectionNumber} ${sectionTitle}`,
      heading: headingLevel,
      spacing: { before: 400, after: 200 }
    })
  );
  
  // Section blocks (with safety check) - limit blocks per section
  if (section.blocks && Array.isArray(section.blocks)) {
    const maxBlocks = 500; // Prevent excessive content
    const blocksToProcess = section.blocks.slice(0, maxBlocks);
    for (const block of blocksToProcess) {
      if (block) {
        const blockElements = convertBlockToDocx(block);
        elements.push(...blockElements);
      }
    }
    if (section.blocks.length > maxBlocks) {
      logger.warn(`[DOCX Export] Section ${section.id} has ${section.blocks.length} blocks, truncated to ${maxBlocks}`);
    }
  }
  
  // Render children recursively (pass the visited set)
  if (section.children && Array.isArray(section.children) && section.children.length > 0) {
    for (const childId of section.children) {
      const childSection = allSections.find(s => s && s.id === childId);
      if (childSection) {
        renderSectionToDocx(childSection, allSections, elements, visited);
      }
    }
  }
}

/**
 * Get DOCX heading level from section number.
 * Section number (e.g., "1.2.3") is the authoritative source for hierarchy depth.
 * "1" → HEADING_1, "1.2" → HEADING_2, "1.2.3" → HEADING_3, etc.
 * Caps at HEADING_6 since DOCX supports up to 6 heading levels.
 */
function getHeadingLevelFromSectionNumber(sectionNumber: string): (typeof HeadingLevel)[keyof typeof HeadingLevel] {
  if (!sectionNumber) return HeadingLevel.HEADING_1;
  const parts = sectionNumber.split('.');
  const depth = Math.min(parts.length, 6);
  
  switch (depth) {
    case 1: return HeadingLevel.HEADING_1;
    case 2: return HeadingLevel.HEADING_2;
    case 3: return HeadingLevel.HEADING_3;
    case 4: return HeadingLevel.HEADING_4;
    case 5: return HeadingLevel.HEADING_5;
    case 6: return HeadingLevel.HEADING_6;
    default: return HeadingLevel.HEADING_1;
  }
}

/**
 * Convert content block to DOCX elements (paragraphs and tables)
 */
function convertBlockToDocx(block: ContentBlock): (Paragraph | Table)[] {
  const elements: (Paragraph | Table)[] = [];
  
  switch (block.type) {
    case 'paragraph':
      elements.push(
        new Paragraph({
          children: [
            new TextRun({ text: (block.content as ParagraphContent).text || '' })
          ],
          spacing: { after: 200 }
        })
      );
      break;
      
    case 'bulleted_list': {
      const bulletItems = (block.content as ListContent).items || [];
      for (const item of bulletItems) {
        elements.push(
          new Paragraph({
            text: item.text || '',
            bullet: { level: 0 },
            spacing: { after: 100 }
          })
        );
      }
      break;
    }
      
    case 'numbered_list': {
      const numberedItems = (block.content as ListContent).items || [];
      for (let i = 0; i < numberedItems.length; i++) {
        elements.push(
          new Paragraph({
            text: numberedItems[i].text || '',
            numbering: { reference: 'default-numbering', level: 0 },
            spacing: { after: 100 }
          })
        );
      }
      break;
    }
      
    case 'table': {
      const tableContent = block.content as TableContent;
      const columns = tableContent.columns || [];
      const rows = tableContent.rows || [];
      
      const tableRows: TableRow[] = [];
      
      // Header row
      tableRows.push(
        new TableRow({
          children: columns.map((col: TableColumn) => 
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: col.label || '', bold: true })
                  ]
                })
              ],
              shading: { type: ShadingType.SOLID, fill: 'D9E2F3', color: 'D9E2F3' } // Light blue header
            })
          )
        })
      );
      
      // Data rows
      for (const row of rows) {
        tableRows.push(
          new TableRow({
            children: columns.map((col: TableColumn) => 
              new TableCell({
                children: [
                  new Paragraph({
                    text: row.cells?.[col.id]?.value || ''
                  })
                ]
              })
            )
          })
        );
      }
      
      if (tableRows.length > 0) {
        elements.push(
          new Paragraph({ text: '' }), // Spacing before table
          new Table({
            rows: tableRows,
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1 },
              bottom: { style: BorderStyle.SINGLE, size: 1 },
              left: { style: BorderStyle.SINGLE, size: 1 },
              right: { style: BorderStyle.SINGLE, size: 1 },
              insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
              insideVertical: { style: BorderStyle.SINGLE, size: 1 }
            }
          }),
          new Paragraph({ text: '' }) // Spacing after table
        );
      }
      break;
    }
      
    case 'heading':
      elements.push(
        new Paragraph({
          children: [
            new TextRun({ 
              text: (block.content as HeadingContent).text || '',
              bold: true,
              size: 28 // 14pt
            })
          ],
          spacing: { before: 300, after: 200 }
        })
      );
      break;
      
    case 'code_block':
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: (block.content as CodeBlockContent).code || '',
              font: 'Courier New',
              size: 20 // 10pt
            })
          ],
          shading: { type: ShadingType.SOLID, fill: 'F5F5F5', color: 'F5F5F5' },
          spacing: { after: 200 }
        })
      );
      break;
      
    case 'image':
      // Image handling would require fetching the image and converting to buffer
      // For now, insert a placeholder
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `[Image: ${(block.content as ImageContent).alt || 'No description'}]`,
              italics: true,
              color: '666666'
            })
          ],
          spacing: { after: 200 }
        })
      );
      break;
      
    case 'divider':
      elements.push(
        new Paragraph({
          border: {
            bottom: {
              color: 'auto',
              space: 1,
              style: BorderStyle.SINGLE,
              size: 6
            }
          },
          spacing: { before: 200, after: 200 }
        })
      );
      break;
      
    case 'vector_graphic': {
      // Handle Mermaid diagrams and other vector graphics
      const vgContent = block.content as VectorGraphicContent;
      // PNG data is added by the prepare-export API for document exports
      const vgWithPng = vgContent as VectorGraphicContent & { 
        _renderedPngBase64?: string; 
        _renderedPngWidth?: number; 
        _renderedPngHeight?: number;
      };
      const pngBase64 = vgWithPng._renderedPngBase64;
      
      logger.debug('[DOCX Export] Processing vector_graphic block:', {
        hasContent: !!vgContent,
        format: vgContent?.format,
        hasPngBase64: !!pngBase64,
        pngLength: pngBase64?.length || 0,
        caption: vgContent?.caption
      });
      
      if (pngBase64) {
        // Decode base64 PNG and embed as image
        try {
          const pngWidth = vgWithPng._renderedPngWidth || 600;
          const pngHeight = vgWithPng._renderedPngHeight || 400;
          
          // Scale image to fill page width
          // Page width is 8.5" with 1" margins = 6.5" content width
          // At 96 DPI (Word's internal resolution), that's 624 pixels
          // docx library uses EMUs internally but accepts pixels for transformation
          const targetWidth = 624; // 6.5 inches at 96 DPI
          const aspectRatio = pngHeight / pngWidth;
          const scaledWidth = targetWidth;
          const scaledHeight = Math.round(targetWidth * aspectRatio);
          
          // Convert base64 to Uint8Array
          const binaryString = atob(pngBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Add image paragraph
          elements.push(
            new Paragraph({
              children: [
                new ImageRun({
                  data: bytes,
                  transformation: {
                    width: scaledWidth,
                    height: scaledHeight
                  },
                  type: 'png'
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { before: 200, after: 100 }
            })
          );
          
          // Add caption if present
          if (vgContent.caption || vgContent.figureNumber) {
            const captionText = vgContent.figureNumber 
              ? `${vgContent.figureNumber}: ${vgContent.caption || ''}`
              : vgContent.caption || '';
            elements.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: captionText,
                    italics: true,
                    size: 20 // 10pt
                  })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 }
              })
            );
          }
        } catch (e) {
          logger.error('[DOCX Export] Failed to embed diagram image:', e);
          elements.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `[Diagram: ${vgContent.caption || 'Failed to render'}]`,
                  italics: true,
                  color: '666666'
                })
              ],
              spacing: { after: 200 }
            })
          );
        }
      } else {
        // No pre-rendered PNG available - show placeholder
        elements.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `[Diagram: ${vgContent.caption || 'Not rendered - export requires pre-rendering'}]`,
                italics: true,
                color: '666666'
              })
            ],
            spacing: { after: 200 }
          })
        );
      }
      break;
    }
      
    default:
      logger.warn('[DOCX Export] Unknown block type:', block.type, 'content:', JSON.stringify(block.content).slice(0, 200));
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `[Unsupported block type: ${block.type}]`,
              italics: true,
              color: '999999'
            })
          ],
          spacing: { after: 200 }
        })
      );
  }
  
  return elements;
}
